import { DateTimeFormatType, DateTimeType } from "DateTimeConstants";
import { getLanguageCode } from "LanguageService";
import type { LocaleDateTimeFormatConfig, ParsedDateResult } from "LocaleSettings";
import moment, { type Duration, type DurationInputArg1, type DurationInputArg2, type Moment } from "moment-timezone";
import type { ValidDateBrand } from "utils-app-container";
import type { DateTimeFormat } from "wtg-material-ui";

// Replace the default way moment handles two-digit years
// see https://momentjs.com/docs/#/parsing/string-format/#Parsing%20two%20digit%20years
moment.parseTwoDigitYear = convert2DigitYear;

export type { Duration };

export const localTimezoneOffset = new Date().getTimezoneOffset();
export const zeroDurationString = "0:00";
export const invalidOffset = 13 * 60 + 59;

export function isDuration(value: unknown): value is Duration {
  return moment.isDuration(value);
}

export function durationEpoch(): Moment {
  return moment([1901, 0, 1]);
}

export function emptyDuration(): Duration {
  return moment.duration();
}

export function createDuration(inp: DurationInputArg1, unit?: DurationInputArg2): Duration {
  return moment.duration(inp, unit);
}

export function maxDuration(): Duration {
  return moment.duration("999:59:00");
}

export function maxDurationAsDateTime(): Moment {
  return durationEpoch().add(maxDuration());
}

export function timeEpoch(): Moment {
  return moment([1900, 0, 1]);
}

export function minValue(): Moment {
  return moment.utc([1, 0, 1]);
}

export function durationFromDate(date: Date | Moment): Duration {
  const base = moment(date).year(durationEpoch().year());
  const difference = base.diff(durationEpoch());
  return moment.duration(difference);
}

export function formatStringForDateTimeType(dateTimeType: DateTimeType): string {
  switch (dateTimeType) {
    case DateTimeType.Date:
      /*! SuppressStringValidation String validation suppressed in initial refactor */
      return "DD-MMM-YY";

    case DateTimeType.Duration:
      throw new Error("DateTimeType of Duration is not supported.");

    case DateTimeType.Time:
      return "HH:mm";

    default:
      return "DD-MMM-YY HH:mm";
  }
}

export function ensureIsDate(value: unknown): Date {
  if (value instanceof Date) {
    return value;
  }
  throw new Error("Expected a Date value.");
}

// Type brand for one sided type predicate
declare abstract class NonMinDateBrand {
  private static readonly $: unique symbol;
  private readonly [NonMinDateBrand.$]: never;
}

export function isNonMinDate(date: unknown): date is Date & NonMinDateBrand {
  return isValidDate(date) && !minValue().isSame(date);
}

// Branded type used for one-sided type predicate.
// isValidDate is used in utils-app-container appContainerContext.extensions.dateExtensions
// so we need to import the type brand from there for it to be compatible with the interface.
export function isValidDate(date: unknown): date is Date & ValidDateBrand {
  return date instanceof Date && !isNaN(date.getTime()) && date.getFullYear() >= 1;
}

function isCurrentCultureUS(): boolean {
  return getLanguageCode() === "EN-US";
}

export function formatDate(dateOrMoment: Date | Moment, formatString: string): string {
  let formattable: moment.Moment;
  if (dateOrMoment instanceof Date) {
    if (dateOrMoment.getTimezoneOffset() !== localTimezoneOffset) {
      // Do not use add/subtract with offset because that can be affected by daylight savings.
      formattable = moment(
        new Date(
          dateOrMoment.getFullYear(),
          dateOrMoment.getMonth(),
          dateOrMoment.getDate(),
          dateOrMoment.getHours(),
          dateOrMoment.getMinutes(),
          dateOrMoment.getSeconds(),
          dateOrMoment.getMilliseconds(),
        ),
      );
    } else {
      formattable = moment(dateOrMoment);
    }
  } else {
    formattable = dateOrMoment;
  }

  return formattable.format(formatString);
}

// The function combines date and time formats, and filter out formats that do not match value
function getApplicableDateTimeFormats(
  dateFormats: readonly string[],
  timeFormats: readonly string[],
  value: string,
): string[] {
  const dateTimeFormats: string[] = [];
  const zIndex = value.indexOf("Z");
  const plusIndex = value.indexOf("+");
  for (const dateFormat of dateFormats) {
    if (value.length < dateFormat.length) {
      continue;
    }

    let hasT = false;
    if (value.length > dateFormat.length) {
      hasT = value[dateFormat.length] === "T";
      if (!hasT && value[dateFormat.length] !== " ") {
        continue;
      }
    }

    if (!getDateFormatRegex(dateFormat).test(value)) {
      continue;
    }

    if (value.length === dateFormat.length) {
      dateTimeFormats.push(dateFormat);
      continue;
    }

    if (!isDateFormatComplete(dateFormat)) {
      continue;
    }

    let timezoneSymbolIndex = -1;
    if (zIndex > 0) {
      timezoneSymbolIndex = zIndex;
    } else if (plusIndex > 0) {
      timezoneSymbolIndex = plusIndex;
    } else {
      const minusIndex = value.indexOf("-", dateFormat.length + 1);
      timezoneSymbolIndex = minusIndex;
    }
    // skip whitespace and T, build valueTimePart until +/-/Z
    const valueTimePart = value.substring(
      dateFormat.length + 1,
      timezoneSymbolIndex === -1 ? value.length + 1 : timezoneSymbolIndex,
    );

    for (const timeFormat of timeFormats) {
      // "H.m" match "2.34", "Z" match "+08:00",
      // only makes short format match long value,
      // but nothing makes long format match short value
      const formatLength = timeFormat.length + dateFormat.length + (timezoneSymbolIndex === -1 ? 1 : 2);
      if (value.length < formatLength) {
        continue;
      }

      if (!getTimeFormatRegex(timeFormat).test(valueTimePart)) {
        continue;
      }

      if (!hasT) {
        dateTimeFormats.push(`${dateFormat} ${timeFormat}`);
      } else if (isTimeFormatComplete(timeFormat)) {
        if (timezoneSymbolIndex === -1) {
          dateTimeFormats.push(`${dateFormat}T${timeFormat}`);
        } else {
          dateTimeFormats.push(`${dateFormat}T${timeFormat}Z`);
        }
      }
    }
  }

  return dateTimeFormats;
}
/*! StartNoStringValidationRegion date formats and regexs */
const requiredDateFormats: readonly string[] = ["D", "M", "YY"];
const requiredTimeFormats: readonly string[] = ["H", "m", "s"];
function isDateFormatComplete(format: string): boolean {
  return requiredDateFormats.every((requiredFormat) => format.includes(requiredFormat));
}
function isTimeFormatComplete(format: string): boolean {
  return requiredTimeFormats.every((requiredFormat) => format.includes(requiredFormat));
}
const formatCache = new Map<string, RegExp>();
function getDateFormatRegex(dateFormat: string): RegExp {
  const cache = formatCache.get(dateFormat);
  if (cache) {
    return cache;
  }
  const dateFormatRegexString = dateFormat
    .replaceAll(".", "\\.")
    .replaceAll("DD", "[0-3]{1}[0-9]{1}")
    .replaceAll("D", "[0-9]{1}")
    .replaceAll("MMM", "[a-zA-Z]{3}")
    .replaceAll("MM", "[0-1]{1}[0-9]{1}")
    .replaceAll("M", "[0-9]{1}")
    .replaceAll("Y", "[0-9]{1}");
  const dateFormatRegex = new RegExp("^" + dateFormatRegexString + ".*$");
  formatCache.set(dateFormat, dateFormatRegex);
  return dateFormatRegex;
}
function getTimeFormatRegex(timeFormat: string): RegExp {
  const cache = formatCache.get(timeFormat);
  if (cache) {
    return cache;
  }
  const timeFormatRegexString = timeFormat
    .replaceAll(".", "\\.")
    .replaceAll("HH", "[0-2]{1}[0-9]{1}")
    .replaceAll("H", "[0-9]{1,2}")
    .replaceAll("mm", "[0-5]{1}[0-9]{1}")
    .replaceAll("m", "[0-9]{1,2}")
    .replaceAll("ss", "[0-5]{1}[0-9]{1}")
    .replaceAll("s", "[0-9]{1,2}")
    .replaceAll("S", "[0-9]{1}");
  const timeFormatRegex = new RegExp("^" + timeFormatRegexString + "$");
  formatCache.set(timeFormat, timeFormatRegex);
  return timeFormatRegex;
}
/*! EndNoStringValidationRegion */

function getDateFormatsSeparatedBy(separator: string, isCultureUS: boolean): string[] {
  // Define base components of a date
  /*! StartNoStringValidationRegion date formats */
  const day1 = "D";
  const day2 = "DD";
  const month1 = "M";
  const month2 = "MM";
  const year2 = "YY";
  const year4 = "YYYY";
  const monthName = "MMM";
  /*! EndNoStringValidationRegion */

  // Construct date formats based on the cultural convention
  const dateFormats: string[] = [];
  const dateFormatsWithMonthName: string[] = [];
  [day2, day1].forEach((day) => {
    [year4, year2].forEach((year) => {
      [month2, month1].forEach((month) => {
        if (isCultureUS) {
          // U.S. formats: Month-Day-Year
          dateFormats.push(`${month}${separator}${day}${separator}${year}`);
        } else {
          // European formats: Day-Month-Year
          dateFormats.push(`${day}${separator}${month}${separator}${year}`);
        }
      });

      dateFormatsWithMonthName.push(`${monthName}${separator}${day}${separator}${year}`);
      dateFormatsWithMonthName.push(`${day}${separator}${monthName}${separator}${year}`);
    });

    [month2, month1].forEach((month) => {
      if (isCultureUS) {
        // U.S. formats: Month-Day
        dateFormats.push(`${month}${separator}${day}`);
      } else {
        // European formats: Day-Month
        dateFormats.push(`${day}${separator}${month}`);
      }
      // ISO formats: Year-Month-Day
      dateFormats.push(`${year4}${separator}${month}${separator}${day}`);
    });

    dateFormatsWithMonthName.push(`${monthName}${separator}${day}`);
    dateFormatsWithMonthName.push(`${day}${separator}${monthName}`);
  });

  return dateFormats.concat(dateFormatsWithMonthName);
}

function getDateFormatsFactory(): (isCultureUS: boolean) => string[] {
  /*! StartNoStringValidationRegion date and time formats */
  const separators = ["-", "/", " ", "", "."];
  /*! EndNoStringValidationRegion */
  const caches: Map<boolean, string[]> = new Map();

  return (isCultureUS: boolean) => {
    const cache = caches.get(isCultureUS);
    if (cache) {
      return cache;
    }

    const formats = separators.reduce<string[]>(
      (acc, separator) => [...acc, ...getDateFormatsSeparatedBy(separator, isCultureUS)],
      [],
    );
    caches.set(isCultureUS, formats);
    return formats;
  };
}

function getDateFormatsByCurrentCulture(dateTimeFormatType: DateTimeFormatType, value: string): string[] {
  const dateFormatsFactory = getDateFormatsFactory();
  const dateFormats = dateFormatsFactory(isCurrentCultureUS());

  const dateAndTimeFormats = getApplicableDateTimeFormats(dateFormats, timeFormats, value).concat(otherFormats);
  switch (dateTimeFormatType) {
    case DateTimeFormatType.Duration:
      throw new Error("DateTimeType of Duration is not supported.");
    case DateTimeFormatType.Time:
      // Some places expect a Time type data to be parsed as Datetime, e.g. https://devops.wisetechglobal.com/wtg/Glow/_git/Glow?path=%2FDotNet%2FHTML%2FClient%2FClient%2FJS%2FShared%2F__tests__%2FFormatIndexGridData.test.js&version=GBmaster&line=183&lineEnd=184&lineStartColumn=1&lineEndColumn=1&lineStyle=plain&_a=contents
      return timeFormats.concat(dateAndTimeFormats);
    default:
      return dateAndTimeFormats;
  }
}
/*! StartNoStringValidationRegion date and time formats */
const timeFormats: readonly string[] = [
  "HHmm",
  "Hm",
  "HH:mm",
  "H:m",
  "HH.mm",
  "H.m",
  "HH:mm:ss",
  "H:m:s",
  "HH:mm:ss.S",
  "H:m:s.S",
  "HH:mm:ss.SS",
  "H:m:s.SS",
  "HH:mm:ss.SSS",
  "H:m:s.SSS",
];
const otherFormats: readonly string[] = ["YYYY-MM-DDTHH:mm:ss.SSSSSSS", "YYYY"];
/*! EndNoStringValidationRegion */

function parseISODuration(input: string, formats: string[]): Moment {
  // Check if the input is a valid ISO 8601 duration, e.g. P3Y6M4DT12H30M5S (3 years, 6 months, 4 days, 12 hours, 30 minutes, 5 seconds)
  if (!/^P(?=\d|T\d)(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/.test(input)) {
    return moment.invalid();
  }

  const duration = moment.duration(input);
  if (duration.asMilliseconds() <= 0) {
    return moment.invalid();
  }

  return moment(durationToString(duration), formats, true);
}

/**
 * Parse ISO date string to moment object
 * @param value ISO format string to be parsed. e.g. "2024-04-01T11:12:13.123Z"
 * @param dateTimeType options: Date / DateTime / DateTimeUtc
 * @param timezone The timezone of the final result. If omitted, the browser timezone is used.
 * @returns moment object
 */
export function parseISODate(value: string, dateTimeType?: DateTimeType, timezone?: string): Moment {
  return parseDate(value, dateTimeType, timezone, isoDateFormatConfig);
}
const isoDateFormatConfig: LocaleDateTimeFormatConfig = {
  getParseDateFormats: () => isoDateFormats,
};
const isoDateFormats: string[] = [
  "YYYY-MM-DD",
  "YYYY-MM-DDTHH:mm:ss",
  "YYYY-MM-DDTHH:mm:ssZ",
  "YYYY-MM-DDTHH:mm:ss.SSS",
  "YYYY-MM-DDTHH:mm:ss.SSSZ",
];

export function parseDate(
  value: string,
  dateTimeType?: DateTimeType,
  timezone?: string,
  dateformatConfig?: LocaleDateTimeFormatConfig,
): Moment {
  if (!value) {
    return moment.invalid();
  }

  const dateTimeFormatType = getDateTimeFormatType(dateTimeType);
  const formats = dateformatConfig?.getParseDateFormats
    ? dateformatConfig?.getParseDateFormats(dateTimeFormatType)
    : getDateFormatsByCurrentCulture(dateTimeFormatType, value);

  let result: Moment;

  if (dateTimeType === DateTimeType.DateTimeUtc) {
    // Using strict mode to avoid parsing invalid formats, e.g. '12Batch' as a date
    result = moment.utc(value, formats, true);
    result = timezone ? result.tz(timezone) : result.local();
  } else {
    // When input is in UTC format, parse it as UTC, e.g. https://devops.wisetechglobal.com/wtg/Glow/_git/Glow?path=%2FDotNet%2FHTML%2FClient%2FClient%2FJS%2FShared%2FVueExtenders%2F__tests__%2FWtgModuleSearchFormatter.test.ts&version=GBmaster&line=19&lineEnd=19&lineStartColumn=57&lineEndColumn=60&lineStyle=plain&_a=contents
    if (timezone) {
      result = moment.tz(value, formats, true, timezone);
    } else {
      result = value.endsWith("Z") ? moment.utc(value, formats, true) : moment(value, formats, true);
    }

    if (dateTimeType === DateTimeType.Time) {
      if (!result.isValid()) {
        // Some projects expect an ISO duration string to be parsed as Time, e.g. https://devops.wisetechglobal.com/wtg/Glow/_git/Glow?path=%2FDotNet%2FHTML%2FClient%2FClient%2FJS%2FShared%2F__tests__%2FEntityStrategyProvider.test.js&version=GBmaster&line=469&lineEnd=469&lineStartColumn=1&lineEndColumn=6&lineStyle=plain&_a=contents
        result = parseISODuration(value, formats);
      }
      const epoch = timeEpoch();
      result.year(epoch.year());
      result.month(epoch.month());
      result.date(epoch.date());
    }
  }

  // Do not need seconds info from the parsed date
  result.seconds(0).milliseconds(0);
  return result;
}

function getDateTimeFormatType(dateTimeType?: DateTimeType): DateTimeFormatType {
  switch (dateTimeType) {
    case DateTimeType.Time:
      return DateTimeFormatType.Time;
    case DateTimeType.Duration:
      return DateTimeFormatType.Duration;
    default:
      return DateTimeFormatType.DateAndTime;
  }
}

export function parseDurationExpression(input: string): Moment | null {
  const matches = /^\s*((\d{0,3})\s*:\s*(\d{0,2})|0+)\s*$/.exec(input);
  if (!matches) {
    return null;
  }

  const minutes = matches[3] ? parseInt(matches[3], 10) : 0;
  if (minutes > 59) {
    return null;
  }

  const hours = matches[2] ? parseInt(matches[2], 10) : 0;
  /*! SuppressStringValidation Time units */
  return durationEpoch().add(minutes, "m").add(hours, "h");
}

export function parseDurationExpressionWithDays(input: string): Moment | null {
  input = input.trim();
  if (!input) {
    return null;
  }

  const [days, hours, minutes] = input
    .slice()
    .replace(/\s+/g, "")
    .split(/[-/:\\]/);

  let validDays = 0;
  let validHours = 0;
  let validMinutes = 0;

  if (days) {
    const hasNonDigits = /\D/.exec(days);
    if (!hasNonDigits) {
      validDays = Number(days);
    } else {
      return null;
    }
  }

  if (hours) {
    const hasNonDigits = /\D/.exec(hours);
    if (!hasNonDigits) {
      validHours = Number(hours);
    } else {
      return null;
    }
  }

  if (minutes) {
    const hasNonDigits = /\D/.exec(minutes);
    if (!hasNonDigits) {
      validMinutes = Number(minutes);
    } else {
      return null;
    }
  }

  if (Number(validDays) > 99 || Number(validHours) > 23 || Number(validMinutes) > 59) {
    return null;
  }

  /*! SuppressStringValidation Time units */
  return durationEpoch().add(minutes, "m").add(hours, "h").add(days, "d");
}

export function parseDateExpression(
  expression: string,
  dateTimeType?: DateTimeType,
  timezone?: string,
  dateformatConfig?: LocaleDateTimeFormatConfig,
): Moment {
  if (expression && dateTimeType !== DateTimeType.Time) {
    expression = expression.toLowerCase().replace(/ /g, "");

    if (expression) {
      const getExpression = (expr: string): string => {
        expr = expr.substring(1);
        if (expr === "") {
          /*! SuppressStringValidation String validation suppressed in initial refactor */
          expr = "+0d";
        }
        return expr;
      };

      /*! SuppressStringValidation minute is valid param */
      const date = dateTimeType === DateTimeType.Date ? today(timezone) : getNow(timezone).startOf("minute");
      switch (expression.charAt(0)) {
        case "y":
          /*! SuppressStringValidation String validation suppressed in initial refactor */
          date.subtract(1, "d");
          expression = getExpression(expression);
          break;

        case "t":
          expression = getExpression(expression);
          break;
      }

      if (date.isValid()) {
        const parsed = parseExpressionCore(expression, date, dateformatConfig);

        if (parsed.isValid()) {
          /*! SuppressStringValidation day param */
          return dateTimeType === DateTimeType.Date ? parsed.startOf("day") : parsed;
        }
      }
    }
  }

  return moment.invalid();
}

/**
 * @description Parses a numeric shortcut string into a moment object. such as "04012024" or "010224 1112".
 * @param value The numeric shortcut string to parse.
 * @param parseNumericShortcutLocale A function that can be used to parse the numeric shortcut string. If omitted, the default function will parse the string based on current user language.
 * @param timeZone the tz of moment object should be provide if the 1st parameter comes from a moment object, it prevents the moment object to be created in the wrong timezone.
 * @returns A moment object that represents the parsed date, or an invalid moment object if the input is not a valid date.
 */
export function parseNumericShortcut(
  value: string,
  parseNumericShortcutLocale?: (value: string) => ParsedDateResult,
  timeZone?: string,
): Moment {
  const shortcutMatches = parseNumericShortcutLocale
    ? parseNumericShortcutLocale(value)
    : parseNumericShortcutDefault(value);

  if (shortcutMatches) {
    const { year, month, day, hour, minute } = shortcutMatches;
    const numericYear = convert2DigitYear(year);
    const numericMonth = Number(month) - 1;
    const numericDay = Number(day);

    if (numericDay <= moment([numericYear, numericMonth]).daysInMonth()) {
      return timeZone
        ? moment.tz([numericYear, numericMonth, numericDay, hour || 0, minute || 0], timeZone)
        : moment([numericYear, numericMonth, numericDay, hour || 0, minute || 0]);
    }
  }

  return moment.invalid();
}

function parseNumericShortcutDefault(value: string): ParsedDateResult {
  const isCultureUS = isCurrentCultureUS();
  const pattern = isCultureUS
    ? /^(?<month>0[1-9]|1[0-2])(?<separator>[./,\\-]?)(?<day>0[1-9]|[1-2][0-9]|3[0-1])\2(?<year>\d{2}|\d{4})(\s(?<hour>0\d|1\d|2[0-3])(?<minute>[0-5]\d))?$/
    : /^(?<day>0[1-9]|[1-2][0-9]|3[0-1])(?<separator>[./,\\-]?)(?<month>0[1-9]|1[0-2])\2(?<year>\d{2}|\d{4})(\s(?<hour>0\d|1\d|2[0-3])(?<minute>[0-5]\d))?$/;

  const matches = pattern.exec(value);

  if (matches && matches.length === 8) {
    const {
      groups: { month, day, year, hour, minute },
    } = matches as unknown as Matches;

    return { year, month, day, hour, minute };
  }

  return undefined;
}

interface Matches {
  groups: {
    month: string;
    day: string;
    year: string;
    hour?: string;
    minute?: string;
  };
}

function convert2DigitYear(year: string): number {
  const yearValue = Number(year);
  if (year.length === 2) {
    return (yearValue < 50 ? 2000 : 1900) + yearValue;
  }
  return yearValue;
}

export function toDateTimeTypeString(dateOrMoment: Date | Moment, dateTimeType: DateTimeType): string {
  if (dateTimeType === DateTimeType.Duration) {
    return durationFromDate(dateOrMoment).toString();
  } else {
    const formatString = formatStringForDateTimeType(dateTimeType);
    return formatDate(dateOrMoment, formatString);
  }
}

/**
 * @description Represents today's date with hours, minutes & seconds set to 00.
 * @param timezone The timezone that should be used to determine the today value. If omitted, the default timezone is used
 * @returns A moment object that represents today, either for the current timezone or the passed in timezone
 */
export function today(timezone?: string): Moment {
  /*! SuppressStringValidation unit of time */
  return getNow(timezone).startOf("day");
}

/**
 * @description Represents the current instance in time.
 * @param timezone The timezone that should be used to determine the now value
 * @returns A moment object that represents now, either for the current timezone or the passed in timezone
 */
export function getNow(timezone?: string): Moment {
  return timezone ? moment.tz(timezone) : moment();
}

/**
 * @description The timezone offset.
 * @param timezone The timezone that should be used to determine the offset value
 * @returns A number that represents the offset in minutes of the either the current timezone or the passed in timezone. This value uses the native sign of the offset similiar to Date.getTimezoneOffset. E.g. For Sydney 04 2024 16:59:54 GMT+1100 (Australian Eastern Daylight Time) Date().getTimezoneOffset() will return -660 even though it is 660 minutes ahead
 */
export function getOffset(isoValue: string, timezone?: string): number {
  return -1 * (timezone ? moment.tz(isoValue, timezone) : moment(isoValue)).utcOffset();
}

export function todayAsDate(): Date {
  return today().toDate();
}

export function applyUtcOffset(value: Moment, dateTimeType?: DateTimeType): Moment {
  if (dateTimeType !== DateTimeType.DateTimeUtc) {
    /*! SuppressStringValidation String validation suppressed in initial refactor */
    return value.subtract(value.utcOffset(), "m");
  }

  return value;
}

export function durationToString(duration: Duration): string {
  const durationParts = [Math.floor(duration.asHours()), duration.minutes().toString().padStart(2, "0")];
  return durationParts.join(":");
}

export function durationToDate(duration: Duration): Date {
  /*! SuppressStringValidation String validation suppressed in initial refactor */
  return durationEpoch().add(duration.valueOf(), "millisecond").toDate();
}

export function reverseUtcOffset(value: Moment, dateTimeType: DateTimeType): Moment {
  if (dateTimeType !== "DateTimeUtc" && !minValue().isSame(value)) {
    /*! SuppressStringValidation String validation suppressed in initial refactor */
    return value.add(value.utcOffset(), "m");
  }

  return value;
}

export function setHoursAndMinutes(value: Moment, date: Date): Moment {
  value.hours(date.getHours());
  value.minutes(date.getMinutes());
  return value;
}

export function setYearMonthAndDay(value: Moment, date: Date): Moment {
  value.year(date.getFullYear());
  value.month(date.getMonth());
  value.date(date.getDate());
  return value;
}

export function toFilterValueString(value: Date | Moment, dateTimeType?: DateTimeType): string {
  let momentValue = value instanceof Date ? moment(value) : value;
  if (dateTimeType === DateTimeType.DateTimeUtc) {
    momentValue = momentValue.clone().utc();
  }
  return momentValue.format("YYYY-MM-DDTHH:mm:ss");
}

export function trimSeconds(value: Moment): Moment {
  return value.seconds(0).milliseconds(0);
}

export function isToday(localSentDate: Date): boolean {
  const todayDate = new Date();
  return (
    localSentDate.getFullYear() === todayDate.getFullYear() &&
    localSentDate.getMonth() === todayDate.getMonth() &&
    localSentDate.getDate() === todayDate.getDate()
  );
}

export function formatDateStringIntoUTC(date: string): string {
  return date.endsWith("Z") ? date : date + "Z";
}

/**
 * This function converts a moment value to a DateTimeFormat object. The DateTimeFormat is used by the WtgDateTimeFormatter.values method
 * @param momentValue a moment value to convert
 * @returns an DateTimeFormat representation
 */
export function momentToDateTimeFormat(momentValue: Moment): DateTimeFormat {
  if (momentValue.isValid()) {
    return {
      year: momentValue.year(),
      month: momentValue.month() + 1,
      day: momentValue.date(),
      hour: momentValue.hours(),
      minutes: momentValue.minutes(),
      seconds: momentValue.seconds(),
      isValid: true,
    };
  }
  return emptyDateTimeFormat();
}

export function emptyDateTimeFormat(): DateTimeFormat {
  return { year: 0, month: 0, day: 0, hour: 0, minutes: 0, seconds: 0, isValid: false };
}

/**
 * This function converts a date value to a DateTimeFormat object. The DateTimeFormat is used by the WtgDateTimeFormatter.values method
 * @param dateValue a date value to convert
 * @returns an DateTimeFormat representation
 */
export function dateToDateTimeFormat(dateValue: Date): DateTimeFormat {
  if (!dateValue || !isValidDate(dateValue)) {
    return emptyDateTimeFormat();
  }

  return {
    year: dateValue.getFullYear(),
    month: dateValue.getMonth() + 1,
    day: dateValue.getDate(),
    hour: dateValue.getHours(),
    minutes: dateValue.getMinutes(),
    seconds: dateValue.getSeconds(),
    isValid: true,
  };
}

function parseExpressionCore(expression: string, date: Moment, dateformatConfig?: LocaleDateTimeFormatConfig): Moment {
  if (["+", "-"].indexOf(expression.charAt(0)) !== -1) {
    let isValid = false;
    const expPlusArr = expression.split("+");
    expPlusArr.forEach((exp) => {
      const expArr = exp.split("-");

      for (let index = 0; index < expArr.length; index++) {
        const set = expArr[index];
        let num: string, unit: string;
        if (set.slice(-2) === "mo") {
          /*! SuppressStringValidation String validation suppressed in initial refactor */
          unit = "M";
          num = set.substring(0, set.length - 2);
        } else {
          unit = set.slice(-1);
          if (/^\d+$/.test(unit)) {
            num = set;
            /*! SuppressStringValidation String validation suppressed in initial refactor */
            unit = "d";
          } else {
            num = set.substring(0, set.length - 1);
          }
        }

        if (/^\d+$/.test(num)) {
          const value = Number(num) * (index === 0 ? 1 : -1);
          date.add(value, unit as moment.unitOfTime.Base);
          isValid = true;
        }
      }
    });

    return isValid ? date : moment.invalid();
  } else {
    return parseNumericShortcut(expression, dateformatConfig?.parseNumericShortcut, date.tz());
  }
}

/**
 * @description This function changes the timezone of a date value to the specified timezone. If no timezone is specified, the date is returned as is.
 * @param date a date value to change the timezone
 * @param timeZone the timezone to change the date to
 * @returns a moment object that represents the date in the specified timezone
 */
export function changeTimezone(date: Moment, timeZone?: string): Moment {
  if (timeZone) {
    return date.clone().tz(timeZone);
  }
  return date;
}
